import { TZDate } from "@date-fns/tz";
import { addDays, addMonths, differenceInCalendarDays, format, parse } from "date-fns";
import { z } from "zod";

/** A calendar date, `YYYY-MM-DD`. */
export type IsoDate = string;
/** A calendar month, `YYYY-MM`. */
export type IsoMonth = string;

export const homeTimeZone = "Australia/Melbourne";

export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((s) => isValidIsoDate(s), "Invalid date");
export const isoMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);

const isoDatePattern = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Plain arithmetic rather than date-fns: every stored date is validated when the budget
 * loads, so this is on the cold-start path (SPEC §11.4).
 */
export function isValidIsoDate(s: string): boolean {
  const m = isoDatePattern.exec(s);
  if (!m) {
    return false;
  }
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1) {
    return false;
  }
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const daysInMonth = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][
    month - 1
  ] as number;
  return day <= daysInMonth;
}

/** Local-midnight Date for date-fns arithmetic. Never stored. */
function parseIsoDate(date: IsoDate): Date {
  return parse(date, "yyyy-MM-dd", new Date(2000, 0, 1));
}

function toIsoDate(d: Date): IsoDate {
  return format(d, "yyyy-MM-dd");
}

export function todayIso(now: Date = new Date(), timeZone = homeTimeZone): IsoDate {
  return format(new TZDate(now, timeZone), "yyyy-MM-dd");
}

export function monthOf(date: IsoDate): IsoMonth {
  return date.slice(0, 7);
}

export function addDaysIso(date: IsoDate, days: number): IsoDate {
  return toIsoDate(addDays(parseIsoDate(date), days));
}

export function daysBetween(from: IsoDate, to: IsoDate): number {
  return differenceInCalendarDays(parseIsoDate(to), parseIsoDate(from));
}

export function addMonthsIso(month: IsoMonth, months: number): IsoMonth {
  return format(addMonths(parseIsoDate(`${month}-01`), months), "yyyy-MM");
}

export function firstDayOfMonth(month: IsoMonth): IsoDate {
  return `${month}-01`;
}

export function lastDayOfMonth(month: IsoMonth): IsoDate {
  return addDaysIso(firstDayOfMonth(addMonthsIso(month, 1)), -1);
}

/** Inclusive list of months from `from` to `to`. Empty if `to` is before `from`. */
export function monthRange(from: IsoMonth, to: IsoMonth): IsoMonth[] {
  const out: IsoMonth[] = [];
  for (let m = from; m <= to; m = addMonthsIso(m, 1)) {
    out.push(m);
  }
  return out;
}

/** `2026-10` → `October 2026`, or `Oct 2026` when short. */
export function formatMonth(month: IsoMonth, style: "long" | "short" = "long"): string {
  return format(parseIsoDate(`${month}-01`), style === "long" ? "MMMM yyyy" : "MMM yyyy");
}

/** `2026-10-06` → `Tue 6 Oct`, with the year when it isn't `currentYear`. */
export function formatDay(date: IsoDate, currentYear?: number): string {
  const d = parseIsoDate(date);
  return d.getFullYear() === currentYear || currentYear === undefined
    ? format(d, "EEE d MMM")
    : format(d, "EEE d MMM yyyy");
}

/** `2026-10-06` → `06/10/2026`. */
export function formatShortDate(date: IsoDate): string {
  return format(parseIsoDate(date), "dd/MM/yyyy");
}

/** Day of week, 0 = Sunday. */
export function dayOfWeek(date: IsoDate): number {
  return parseIsoDate(date).getDay();
}

export function dateFromParts(year: number, month: number, day: number): IsoDate | null {
  const s = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  return isValidIsoDate(s) ? s : null;
}
